import React, { createContext, useContext } from 'react';
import {
  useGovernanceSocket,
  GovernanceSocketState,
  GovernanceFinalResult,
  GovernanceQuestion,
} from '../hooks/useGovernanceSocket';

// ── Types ──────────────────────────────────────────────────────────────────────

interface GovernanceContextValue {
  state: GovernanceSocketState;
  start: (companyId: string, policyIds: string[], token: string, jurisdiction?: string) => void;
  resume: (sessionId: string, token: string) => void;
  reset: () => void;
  disconnect: () => void;
  setQuestions: (questions: GovernanceQuestion[]) => void;
  setFinalResult: (result: GovernanceFinalResult) => void;
}

// ── Context ────────────────────────────────────────────────────────────────────

const GovernanceContext = createContext<GovernanceContextValue | null>(null);

// ── Provider ───────────────────────────────────────────────────────────────────

/**
 * Wraps the app at the root level so GovernanceStartPage state persists
 * across tab navigation (component unmount / remount) while an analysis
 * is in progress.
 */
export function GovernanceProvider({ children }: { children: React.ReactNode }) {
  const socket = useGovernanceSocket();
  return (
    <GovernanceContext.Provider value={socket}>
      {children}
    </GovernanceContext.Provider>
  );
}

// ── Consumer hook ──────────────────────────────────────────────────────────────

export function useGovernance(): GovernanceContextValue {
  const ctx = useContext(GovernanceContext);
  if (!ctx) throw new Error('useGovernance must be used inside <GovernanceProvider>');
  return ctx;
}
